// Trending Stays This Week — Homepage rail.
//
// Data: `/api/hotels/trending` — anonymized, aggregated, 24h-cached engine.
// Placement: Homepage, below hero search, above Recently Viewed rail.
// Multi-supplier ready: cards render whatever `provider` the backend returns.

import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Star, MapPin, ShieldCheck, Coffee, Percent, ChevronRight, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useCurrency } from '../../context/CurrencyContext';
import NoImagePlaceholder from '../hotels/NoImagePlaceholder';

// Simple city-chip switcher. Only pings the backend once per selection so
// switching is snappy; results are cached for 24h server-side per (city, dates).
const TrendingStays = () => {
  const { formatPrice } = useCurrency();
  const [cities, setCities] = useState(['Dubai', 'Paris', 'Los Angeles', 'Moscow']);
  const [city, setCity] = useState(null);   // null → server auto-detects
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/hotels/trending/cities')
      .then(({ data }) => { if (!cancelled && Array.isArray(data?.cities)) setCities(data.cities); })
      .catch(() => { /* keep defaults */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setHidden(false);
    api.get('/hotels/trending', { params: { city: city || undefined, limit: 10 } })
      .then(({ data }) => { if (!cancelled) setPayload(data); })
      .catch(() => { if (!cancelled) setHidden(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [city]);

  const hotels = payload?.hotels || [];
  const activeCity = payload?.city || city || '—';
  const checkIn = payload?.check_in;
  const checkOut = payload?.check_out;
  const nights = payload?.nights || 1;

  const linkFor = (h) => {
    const p = new URLSearchParams();
    if (activeCity) p.set('city', activeCity);
    if (checkIn)    p.set('check_in', checkIn);
    if (checkOut)   p.set('check_out', checkOut);
    p.set('rooms', '1');
    p.set('guests', '2');
    return `/hotels/${h.id}?${p.toString()}`;
  };

  const skeletons = useMemo(() => Array.from({ length: 6 }), []);

  if (hidden) return null;

  return (
    <section
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
      data-testid="trending-stays"
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-3 mb-5">
        <div>
          <p className="text-[11px] uppercase tracking-widest font-semibold text-red-600 dark:text-red-400 flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Trending this week
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Trending Stays</h2>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            The most-booked, most-viewed properties in {activeCity} · Fresh every 24h
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap" role="tablist" aria-label="Trending destinations">
          {cities.map((c) => (
            <button
              key={c}
              type="button"
              role="tab"
              aria-selected={activeCity.toLowerCase() === c.toLowerCase()}
              onClick={() => setCity(c)}
              data-testid={`trending-city-${c.toLowerCase()}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                activeCity.toLowerCase() === c.toLowerCase()
                  ? 'bg-red-600 border-red-600 text-white'
                  : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-red-300 bg-white dark:bg-gray-900'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div
          className="flex gap-3 sm:gap-4 overflow-x-auto no-scrollbar pb-2 -mx-4 sm:mx-0 px-4 sm:px-0"
          data-testid="trending-skeleton"
        >
          {skeletons.map((_, i) => (
            <div key={i} className="min-w-[240px] sm:min-w-[280px] max-w-[280px] shrink-0 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
              <div className="h-40 bg-gray-200 dark:bg-gray-800 animate-pulse" />
              <div className="p-3 space-y-2">
                <div className="h-3.5 w-3/4 bg-gray-200 dark:bg-gray-800 rounded animate-pulse" />
                <div className="h-3 w-1/2 bg-gray-200 dark:bg-gray-800 rounded animate-pulse" />
                <div className="h-4 w-24 bg-gray-200 dark:bg-gray-800 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : hotels.length === 0 ? (
        <div className="text-sm text-gray-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Curating trending stays…
        </div>
      ) : (
        <div
          className="flex gap-3 sm:gap-4 overflow-x-auto no-scrollbar pb-2 -mx-4 sm:mx-0 px-4 sm:px-0"
          style={{ scrollSnapType: 'x mandatory' }}
        >
          {hotels.map((h, idx) => {
            const img = h.image || (h.images && h.images[0]);
            const stars = Math.max(0, Math.min(5, Math.round(Number(h.rating) || 0)));
            const google = Number(h.google_rating);
            return (
              <div
                key={h.id}
                data-testid={`trending-hotel-${h.id}`}
                className="group relative min-w-[240px] sm:min-w-[280px] max-w-[280px] shrink-0 rounded-xl overflow-hidden bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:shadow-xl transition-all hover:-translate-y-0.5"
                style={{ scrollSnapAlign: 'start' }}
              >
                <Link to={linkFor(h)} className="block">
                  <div className="relative h-40 bg-gray-100 dark:bg-gray-800 overflow-hidden">
                    {img ? (
                      <img
                        src={img}
                        alt={h.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <NoImagePlaceholder />
                    )}
                    <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-black/70 backdrop-blur text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      <Sparkles className="h-2.5 w-2.5" /> #{idx + 1} Trending
                    </span>
                    {h.discount_pct > 0 && (
                      <span className="absolute top-2 right-2 inline-flex items-center gap-0.5 bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                        <Percent className="h-2.5 w-2.5" /> {h.discount_pct}% OFF
                      </span>
                    )}
                  </div>
                  <div className="p-3 space-y-1">
                    <div className="flex items-start gap-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 dark:text-white truncate flex-1">{h.name}</p>
                      {(google > 0 || h.rating > 0) && (
                        <span
                          className="shrink-0 inline-flex items-center gap-0.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-[11px] font-bold px-1.5 py-0.5 rounded"
                          title={google > 0 ? 'Google rating' : 'Guest rating'}
                        >
                          {(google > 0 ? google : h.rating).toFixed(1)} <Star className="h-2.5 w-2.5 fill-current" />
                        </span>
                      )}
                    </div>
                    {stars > 0 && (
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: stars }).map((_, i) => (
                          <Star key={i} className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate flex items-center gap-1">
                      <MapPin className="h-3 w-3 flex-shrink-0" />
                      {[h.area, h.city].filter(Boolean).join(', ') || activeCity}
                    </p>
                    <div className="flex items-center flex-wrap gap-1 pt-1" data-testid={`trending-badges-${h.id}`}>
                      {h.free_cancellation && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded">
                          <ShieldCheck className="h-2.5 w-2.5" /> Free cancel
                        </span>
                      )}
                      {h.breakfast_included && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-1.5 py-0.5 rounded">
                          <Coffee className="h-2.5 w-2.5" /> Breakfast
                        </span>
                      )}
                      {h.provider && h.provider !== 'mock' && (
                        <span className="inline-flex items-center text-[10px] font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded uppercase">
                          {h.provider}
                        </span>
                      )}
                    </div>
                    <div className="flex items-baseline justify-between pt-1">
                      <div>
                        <p className="text-sm font-bold text-red-600 dark:text-red-400">
                          {formatPrice(h.display_price)}
                          <span className="text-[10px] text-gray-500 ml-1 font-normal">total ({nights}n)</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
                <div className="px-3 pb-3">
                  <Link
                    to={linkFor(h)}
                    data-testid={`trending-cta-${h.id}`}
                    className="w-full inline-flex items-center justify-center gap-1 text-xs font-semibold py-2 rounded-md bg-red-600 hover:bg-red-700 text-white transition"
                  >
                    View Rooms <ChevronRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default TrendingStays;
